/**
 * Wallet Expenses Context
 *
 * Global context for managing expense records with real-time Firestore sync.
 * Provides instant writes (no debouncing) and real-time updates via onSnapshot.
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef, useState } from 'react';
import { DocumentSnapshot, Unsubscribe, onSnapshot } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { useMaybeOLVRate } from './OLVRateContext';
import type { Expense, ExpenseStatus } from '../types/expenses';
import {
  createExpense,
  onExpensesSnapshot,
  listExpenses,
  CreateExpenseInput,
  type ExpenseStore,
} from '../repositories/walletExpensesRepository';
import { Paths } from '../repositories/firestoreUtils';
import { onAccountMembershipAccountIdSnapshot } from '../repositories/walletRepository';

// ============================================================================
// Types
// ============================================================================

interface WalletExpensesState {
  expenses: Expense[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  hasMore: boolean;
  version: number;
  latestExpense: Expense | null;
}

interface WalletExpensesContextValue {
  state: WalletExpensesState;
  /** Add a new expense record (instant write to Firestore) */
  addExpense: (input: AddExpenseInput) => Promise<Expense | null>;
  /** Load more expenses (pagination) */
  loadMore: () => Promise<void>;
  /** Refresh expenses from Firestore */
  refresh: () => void;
  /** Get an expense by ID */
  getExpenseById: (id: string) => Expense | undefined;
}

export interface AddExpenseInput {
  /** Summary description */
  summary: string;
  /** Amount in USD */
  amountUsd: number;
  /** Tags for categorization */
  tags?: string[];
  /** AI model ID (optional) */
  modelId?: string;
  /** Provider name (optional) */
  provider?: string;
  /** Initial status (defaults to 'completed') */
  status?: ExpenseStatus;
}

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 25;

// ============================================================================
// Reducer
// ============================================================================

type WalletExpensesAction =
  | { type: 'SET_EXPENSES'; payload: { items: Expense[]; hasMore: boolean } }
  | { type: 'APPEND_EXPENSES'; payload: { items: Expense[]; hasMore: boolean } }
  | { type: 'ADD_EXPENSE'; payload: Expense }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR' };

const initialState: WalletExpensesState = {
  expenses: [],
  isLoading: true,
  error: null,
  totalCount: 0,
  hasMore: false,
  version: 0,
  latestExpense: null,
};

function walletExpensesReducer(state: WalletExpensesState, action: WalletExpensesAction): WalletExpensesState {
  switch (action.type) {
    case 'SET_EXPENSES':
      return {
        ...state,
        expenses: action.payload.items,
        totalCount: action.payload.items.length,
        hasMore: action.payload.hasMore,
        isLoading: false,
        error: null,
        version: state.version + 1,
      };
    case 'APPEND_EXPENSES':
      const combined = [...state.expenses, ...action.payload.items];
      return {
        ...state,
        expenses: combined,
        totalCount: combined.length,
        hasMore: action.payload.hasMore,
        isLoading: false,
        version: state.version + 1,
      };
    case 'ADD_EXPENSE':
      const newExpenses = [action.payload, ...state.expenses];
      return {
        ...state,
        expenses: newExpenses,
        totalCount: state.totalCount + 1,
        version: state.version + 1,
        latestExpense: action.payload,
      };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'CLEAR':
      return initialState;
    default:
      return state;
  }
}

// ============================================================================
// Context
// ============================================================================

const WalletExpensesContext = createContext<WalletExpensesContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

export function WalletExpensesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const olvRate = useMaybeOLVRate();
  const [state, dispatch] = useReducer(walletExpensesReducer, initialState);
  const cursorRef = useRef<DocumentSnapshot | null>(null);
  const unsubRef = useRef<Unsubscribe | null>(null);

  // R19 (2026-08-18 Miles: wallet must explain the balance task by task):
  // read the ledger that is actually DEBITED. Workspace-mode accounts debit
  // accounts/{id}/wallet/summary/expenses — reading only the personal store
  // hid every task row and showed nothing but personally-billed background
  // sweeps. The mode comes from the account's displaySummary doc
  // (server-stamped wallet_mode on every debit); missing doc, read error, or
  // no active account all fall back to the personal ledger (pre-R19
  // behavior).
  // Per-org wallet round (2026-08-19): the acting org comes from the SAME
  // back-pointer listener the balance display uses — the old read of
  // AuthContext.user.accountId (hydrated from user_profile.profiles[0])
  // could disagree with the workspace the user actually switched to.
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null);
  useEffect(() => {
    if (!user?.id) {
      setActiveAccountId(null);
      return;
    }
    return onAccountMembershipAccountIdSnapshot(user.id, setActiveAccountId, () => setActiveAccountId(null));
  }, [user?.id]);
  const [expenseStore, setExpenseStore] = useState<ExpenseStore | null>(null);
  useEffect(() => {
    if (!user?.id) {
      setExpenseStore(null);
      return;
    }
    if (!activeAccountId) {
      setExpenseStore(user.id);
      return;
    }
    let cancelled = false;
    const unsub = onSnapshot(
      Paths.walletDisplaySummaryDoc(activeAccountId),
      (snap) => {
        if (cancelled) return;
        const mode = (snap.data() as { wallet_mode?: string } | undefined)?.wallet_mode;
        setExpenseStore(mode === 'workspace' ? { accountId: activeAccountId } : user.id);
      },
      () => {
        if (!cancelled) setExpenseStore(user.id);
      },
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, [user?.id, activeAccountId]);
  const storeKey = expenseStore === null
    ? ''
    : typeof expenseStore === 'string'
      ? `u:${expenseStore}`
      : `a:${expenseStore.accountId}`;
  const storeRef = useRef<ExpenseStore | null>(expenseStore);
  storeRef.current = expenseStore;

  // Set up real-time listener when the resolved ledger changes
  useEffect(() => {
    if (!user?.id || expenseStore === null) {
      dispatch({ type: 'CLEAR' });
      if (user?.id) dispatch({ type: 'SET_LOADING', payload: true });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    // Clean up previous listener
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    // Start real-time sync on the resolved ledger
    unsubRef.current = onExpensesSnapshot(
      expenseStore,
      { limit: PAGE_SIZE },
      (items, nextCursor) => {
        cursorRef.current = nextCursor;
        dispatch({
          type: 'SET_EXPENSES',
          payload: { items, hasMore: items.length >= PAGE_SIZE },
        });
      },
      (error) => {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    );

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
    // expenseStore is captured via storeKey so object identity churn can't
    // resubscribe on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, storeKey]);

  // Add a new expense (instant write)
  const addExpense = useCallback(
    async (input: AddExpenseInput): Promise<Expense | null> => {
      if (!user?.id) return null;

      const rate = olvRate?.getEffectiveRate() || 0.001;
      const memberId = user.profileId || user.id;

      const createInput: CreateExpenseInput = {
        summary: input.summary,
        memberId,
        amountUsd: input.amountUsd,
        amountOlv: input.amountUsd / rate,
        olvRateAtTime: rate,
        tags: input.tags,
        modelId: input.modelId,
        provider: input.provider,
        status: input.status || 'completed',
      };

      try {
        const expense = await createExpense(user.id, createInput);
        // The real-time listener will update the state automatically
        // But we also add it optimistically for instant UI feedback
        dispatch({ type: 'ADD_EXPENSE', payload: expense });
        return expense;
      } catch (error) {
        console.error('[WalletExpensesContext] Failed to create expense:', error);
        dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
        return null;
      }
    },
    [user?.id, user?.profileId, olvRate]
  );

  // Load more expenses (pagination)
  const loadMore = useCallback(async () => {
    if (!user?.id || !cursorRef.current || state.isLoading) return;

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const result = await listExpenses(storeRef.current ?? user.id, {
        limit: PAGE_SIZE,
        startAfter: cursorRef.current,
      });

      cursorRef.current = result.nextCursor;
      dispatch({
        type: 'APPEND_EXPENSES',
        payload: { items: result.items, hasMore: result.items.length >= PAGE_SIZE },
      });
    } catch (error) {
      console.error('[WalletExpensesContext] Failed to load more expenses:', error);
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
    }
  }, [user?.id, state.isLoading]);

  // Refresh expenses
  const refresh = useCallback(() => {
    if (!user?.id) return;

    // Clean up and re-subscribe to trigger a fresh load
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    unsubRef.current = onExpensesSnapshot(
      storeRef.current ?? user.id,
      { limit: PAGE_SIZE },
      (items, nextCursor) => {
        cursorRef.current = nextCursor;
        dispatch({
          type: 'SET_EXPENSES',
          payload: { items, hasMore: items.length >= PAGE_SIZE },
        });
      },
      (error) => {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    );
  }, [user?.id]);

  // Get expense by ID
  const getExpenseById = useCallback(
    (id: string): Expense | undefined => {
      return state.expenses.find((e) => e.id === id);
    },
    [state.expenses]
  );

  const value: WalletExpensesContextValue = {
    state,
    addExpense,
    loadMore,
    refresh,
    getExpenseById,
  };

  return (
    <WalletExpensesContext.Provider value={value}>
      {children}
    </WalletExpensesContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useWalletExpenses(): WalletExpensesContextValue {
  const context = useContext(WalletExpensesContext);
  if (!context) {
    throw new Error('useWalletExpenses must be used within a WalletExpensesProvider');
  }
  return context;
}

/**
 * Optional hook that returns null if not within provider
 */
export function useMaybeWalletExpenses(): WalletExpensesContextValue | null {
  return useContext(WalletExpensesContext);
}
